import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/* Иллюстрация метода, не кейс: облако светящихся точек собирается в цепочку шагов условного процесса,
   скан проходит слева направо и помечает, где нужен ИИ, а где хватит порядка; в одном шаге видна
   утечка ручной работы. Финал стабильный, обратно в хаос не разваливается. Цифр, людей и клиентов
   здесь нет. Узлы и подписи: copy-podbor-hero (copywriter-ru). */
const NODES = [
  { label: "Заявка", ai: false },
  { label: "Ответ", ai: true },
  { label: "Запись", ai: false },
  { label: "Оплата", ai: false },
  { label: "Отчёт", ai: false, leak: true },
  { label: "Выводы", ai: true },
];
const LEAK = NODES.findIndex((n) => n.leak);
const NODE_Y = 0.42;
const nodeFrac = (i) => 0.09 + i * 0.164;
const nodeX = (w, i) => w * nodeFrac(i);
// секунды: хаос, сборка, скан слева направо
const ASSEMBLE_AT = 1.2;
const SCAN_AT = 2.6;
const SCAN_DUR = 1.8;
const ARIA =
  "Схема условного процесса малого бизнеса: заявка, ответ, запись, оплата, отчёт, выводы; ответ и выводы отмечены как места для ИИ, остальные шаги как порядок, а в отчёте видна утечка ручной работы.";

const makeSprite = (r, g, b) => {
  const s = 48;
  const c = document.createElement("canvas");
  c.width = c.height = s;
  const x = c.getContext("2d");
  const gr = x.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  gr.addColorStop(0, `rgba(${r},${g},${b},1)`);
  gr.addColorStop(0.18, `rgba(${r},${g},${b},0.55)`);
  gr.addColorStop(1, `rgba(${r},${g},${b},0)`);
  x.fillStyle = gr;
  x.fillRect(0, 0, s, s);
  return c;
};

export const ParticleField = () => {
  const reduce = useReducedMotion();
  const canvasRef = useRef(null);
  // 0 хаос, 1 собрано (подписи узлов), 2 скан прошёл (легенда, подпись утечки)
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    const sp = {
      blue: makeSprite(62, 139, 255),
      cyan: makeSprite(56, 189, 248),
      white: makeSprite(205, 230, 255),
      red: makeSprite(255, 107, 107),
    };
    const ig = NODES.map(() => 0);
    let w = 0;
    let h = 0;
    let sc = 1;
    let particles = [];
    let raf = 0;
    let running = false;
    let t = 0;
    let last = 0;
    let stageNow = 0;
    let leakT0 = -1;
    let doneT = -1;
    let mx = -1e3;
    let my = -1e3;

    const build = () => {
      // на узком экране точек меньше: дешевле для мобильного GPU
      const narrow = w < 420;
      const per = narrow ? 14 : 22;
      const rail = narrow ? 5 : 8;
      const leakN = narrow ? 9 : 14;
      const base = () => ({ x: w / 2, y: h / 2, vx: 0, vy: 0, hx: Math.random(), hy: Math.random(), s: Math.random() });
      particles = [];
      NODES.forEach((_, ni) => {
        for (let k = 0; k < per; k++) particles.push({ ...base(), role: "node", ni, k, K: per });
      });
      for (let seg = 0; seg < NODES.length - 1; seg++) {
        for (let k = 0; k < rail; k++) particles.push({ ...base(), role: "rail", seg, f: (k + 0.5) / rail });
      }
      for (let k = 0; k < leakN; k++) particles.push({ ...base(), role: "leak", k, K: leakN });
    };

    const resize = () => {
      const r = cv.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const first = !particles.length;
      w = r.width;
      h = r.height;
      sc = Math.min(1, Math.max(0.6, w / 560));
      cv.width = w * dpr;
      cv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (first) build();
    };

    const radius = (ni) => (NODES[ni].ai ? 28 : 22) * sc;
    const ny = () => h * NODE_Y;
    const leaking = (p) => leakT0 >= 0 && (t - leakT0) * 0.4 - p.k / p.K >= 0;

    const target = (p, i) => {
      if (t < ASSEMBLE_AT) {
        const a = p.hx * Math.PI * 2 + t * (0.4 + p.s * 0.4);
        const r = Math.sqrt(p.hy) * 0.5 * Math.min(w, h * 1.1);
        return [w / 2 + Math.cos(a) * r * (1 + 0.15 * Math.sin(t + i)), h / 2 + Math.sin(a) * r * (1 + 0.15 * Math.cos(t * 0.8 + i))];
      }
      if (p.role === "rail") {
        const x0 = nodeX(w, p.seg);
        return [x0 + (nodeX(w, p.seg + 1) - x0) * p.f, ny() + Math.sin(t * 2 + p.f * 9) * 1.2 * sc];
      }
      const ni = p.role === "leak" ? LEAK : p.ni;
      const R = radius(ni) * (p.role === "leak" ? 0.5 : 1);
      const spin = NODES[ni].ai && ig[ni] > 0.5 ? t * 0.6 : 0;
      const a = p.k * 2.399 + spin;
      const r = R * Math.sqrt((p.k + 0.5) / p.K) * (1 + 0.06 * Math.sin(t * 1.6 + p.k));
      return [nodeX(w, ni) + Math.cos(a) * r, ny() + Math.sin(a) * r];
    };

    const frame = (still = false) => {
      const now = performance.now();
      if (!still) {
        t += Math.min((now - last) / 1000, 0.05);
        last = now;
      }
      const scanPos = Math.min(1, Math.max(0, (t - SCAN_AT) / SCAN_DUR));
      const beamX = nodeX(w, 0) - 30 + scanPos * (nodeX(w, NODES.length - 1) - nodeX(w, 0) + 60);
      NODES.forEach((_, i) => {
        const goal = t >= SCAN_AT && beamX >= nodeX(w, i) ? 1 : 0;
        ig[i] += (goal - ig[i]) * (still ? 1 : 0.08);
      });
      if (leakT0 < 0 && ig[LEAK] > 0.5) leakT0 = t;
      if (scanPos >= 1 && doneT < 0) doneT = t;
      const nextStage = scanPos >= 1 ? 2 : t >= ASSEMBLE_AT ? 1 : 0;
      if (nextStage !== stageNow) {
        stageNow = nextStage;
        setStage(nextStage);
      }

      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";

      // световая линия трассы и скан
      if (t >= ASSEMBLE_AT) {
        const a = Math.min(1, (t - ASSEMBLE_AT) / 1);
        const g = ctx.createLinearGradient(nodeX(w, 0), 0, nodeX(w, NODES.length - 1), 0);
        g.addColorStop(0, "rgba(62,139,255,0)");
        g.addColorStop(0.15, "rgba(62,139,255,0.5)");
        g.addColorStop(0.85, "rgba(56,189,248,0.5)");
        g.addColorStop(1, "rgba(56,189,248,0)");
        ctx.globalAlpha = a * 0.7;
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(nodeX(w, 0), ny());
        ctx.lineTo(nodeX(w, NODES.length - 1), ny());
        ctx.stroke();
        if (scanPos > 0 && scanPos < 1) {
          // тонкая вертикальная линия-скан, гаснет к краям по высоте
          const b = ctx.createLinearGradient(0, h * 0.14, 0, h * 0.78);
          b.addColorStop(0, "rgba(156,200,255,0)");
          b.addColorStop(0.5, "rgba(156,200,255,0.9)");
          b.addColorStop(1, "rgba(156,200,255,0)");
          ctx.globalAlpha = 1;
          ctx.strokeStyle = b;
          ctx.lineWidth = 2;
          ctx.shadowColor = "rgba(62,139,255,0.9)";
          ctx.shadowBlur = 14;
          ctx.beginPath();
          ctx.moveTo(beamX, h * 0.14);
          ctx.lineTo(beamX, h * 0.78);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
        // ореолы узлов после скана
        NODES.forEach((n, i) => {
          if (ig[i] < 0.01) return;
          const size = (n.ai ? 110 : 72) * sc;
          ctx.globalAlpha = (n.ai ? 0.4 : 0.18) * ig[i];
          ctx.drawImage(n.ai ? sp.cyan : sp.blue, nodeX(w, i) - size / 2, ny() - size / 2, size, size);
          if (n.leak) {
            ctx.globalAlpha = 0.22 * ig[i];
            ctx.drawImage(sp.red, nodeX(w, i) - size / 2, ny() - size / 2, size, size);
          }
        });
      }

      particles.forEach((p, i) => {
        if (p.role === "leak" && leaking(p)) {
          // капли уходят вниз и гаснут: потеря ручной работы
          const life = ((t - leakT0) * 0.4 - p.k / p.K) % 1;
          p.x = nodeX(w, LEAK) + Math.sin(p.k * 7.3) * 9 * sc;
          p.y = ny() + radius(LEAK) + life * h * 0.3;
          ctx.globalAlpha = (1 - life) * Math.min(1, life * 8);
          ctx.drawImage(sp.red, p.x - 4.5 * sc, p.y - 4.5 * sc, 9 * sc, 9 * sc);
          return;
        }
        const [tx, ty] = target(p, i);
        p.vx += (tx - p.x) * 0.012;
        p.vy += (ty - p.y) * 0.012;
        p.vx *= 0.86;
        p.vy *= 0.86;
        const dx = p.x - mx;
        const dy = p.y - my;
        const d = Math.hypot(dx, dy);
        if (d > 0.01 && d < 80) {
          p.vx += (dx / d) * 1.5;
          p.vy += (dy / d) * 1.5;
        }
        p.x += p.vx;
        p.y += p.vy;
        const built = t >= ASSEMBLE_AT;
        if (!built) {
          ctx.globalAlpha = 0.35 + p.s * 0.55;
          const size = 8 + p.s * 10;
          ctx.drawImage(p.s > 0.75 ? sp.cyan : sp.blue, p.x - size / 2, p.y - size / 2, size, size);
        } else if (p.role === "rail") {
          ctx.globalAlpha = 0.5;
          ctx.drawImage(sp.cyan, p.x - 2.5 * sc, p.y - 2.5 * sc, 5 * sc, 5 * sc);
        } else {
          const ni = p.role === "leak" ? LEAK : p.ni;
          const ai = NODES[ni].ai;
          const lit = ig[ni];
          const size = ((ai ? 7 : 6) + lit * (ai ? 5 : 1.5)) * sc;
          ctx.globalAlpha = 0.4 + 0.5 * lit;
          ctx.drawImage(ai && lit > 0.3 ? sp.white : sp.blue, p.x - size / 2, p.y - size / 2, size, size);
        }
      });

      // пакеты света бегут по трассе; после утечки поток слабее
      if (!still && doneT >= 0) {
        const x0 = nodeX(w, 0);
        const span = nodeX(w, NODES.length - 1) - x0;
        for (let j = 0; j < 3; j++) {
          const u = ((t - doneT) * 0.16 + j / 3) % 1;
          const x = x0 + u * span;
          const f = x > nodeX(w, LEAK) ? 0.65 : 1;
          for (let q = 4; q >= 0; q--) {
            ctx.globalAlpha = q === 0 ? 0.95 * f : (0.4 / q) * f;
            const size = (q === 0 ? 16 : 10) * sc * f;
            ctx.drawImage(q === 0 ? sp.white : sp.blue, x - q * 7 * sc - size / 2, ny() - size / 2, size, size);
          }
        }
      }
      raf = running && !still ? requestAnimationFrame(() => frame()) : 0;
    };

    const start = () => {
      if (running) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(() => frame());
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    // reduce-motion: сразу собранный финал одним кадром, без анимации
    const still = () => {
      t = SCAN_AT + SCAN_DUR + 2;
      leakT0 = t - 1.5;
      doneT = t;
      particles.forEach((p, i) => {
        [p.x, p.y] = target(p, i);
      });
      frame(true);
    };

    const onMove = (e) => {
      const r = cv.getBoundingClientRect();
      mx = e.clientX - r.left;
      my = e.clientY - r.top;
    };
    const onLeave = () => {
      mx = my = -1e3;
    };

    resize();
    let io;
    let ro;
    let sync;
    if (reduce) {
      still();
      ro = new ResizeObserver(() => {
        resize();
        still();
      });
      ro.observe(cv);
    } else {
      ro = new ResizeObserver(resize);
      ro.observe(cv);
      // рисуем только пока блок на экране и вкладка видима
      let inView = true;
      sync = () => (inView && !document.hidden ? start() : stop());
      io = new IntersectionObserver(([e]) => {
        inView = e.isIntersecting;
        sync();
      });
      io.observe(cv);
      document.addEventListener("visibilitychange", sync);
      cv.addEventListener("pointermove", onMove);
      cv.addEventListener("pointerleave", onLeave);
      sync();
    }

    return () => {
      stop();
      ro.disconnect();
      io?.disconnect();
      if (sync) document.removeEventListener("visibilitychange", sync);
      cv.removeEventListener("pointermove", onMove);
      cv.removeEventListener("pointerleave", onLeave);
    };
  }, [reduce]);

  const on = (s) => (stage >= s ? "opacity-100" : "opacity-0");

  return (
    <figure className="mx-auto w-full max-w-[560px]" data-testid="hero-process-map">
      <div role="img" aria-label={ARIA} className="relative aspect-[16/10] w-full">
        <canvas ref={canvasRef} className="block h-full w-full" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {NODES.map((n, i) => (
            <span
              key={n.label}
              style={{ left: `${nodeFrac(i) * 100}%`, top: `${NODE_Y * 100}%` }}
              className={`absolute -translate-x-1/2 -translate-y-[48px] whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] transition-opacity duration-700 ease-smooth sm:-translate-y-[60px] sm:text-xs ${on(1)} ${
                n.ai ? "text-accent-soft" : "text-soft"
              }`}
            >
              {n.label}
            </span>
          ))}
          <span
            style={{ left: `${nodeFrac(LEAK) * 100}%`, top: "86%" }}
            className={`absolute -translate-x-1/2 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] text-danger transition-opacity duration-700 ease-smooth sm:text-xs ${on(2)}`}
          >
            ручная работа
          </span>
        </div>
      </div>
      <div
        className={`mt-4 flex items-center justify-center gap-5 text-sm text-muted transition-opacity duration-700 ease-smooth ${on(2)}`}
        aria-hidden="true"
      >
        <span className="inline-flex items-center gap-2">
          <i className="h-2 w-2 rounded-full bg-ai shadow-[0_0_10px_rgba(205,230,255,0.9)]" />
          нужен ИИ
        </span>
        <span className="inline-flex items-center gap-2">
          <i className="h-2 w-2 rounded-full bg-accent shadow-[0_0_10px_rgba(62,139,255,0.9)]" />
          хватит порядка
        </span>
      </div>
      <figcaption className="mt-3 text-center text-sm text-soft">
        Условный процесс, не чей-то кейс. Так разбор проходит по каждому шагу.
      </figcaption>
    </figure>
  );
};
